import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const checkAuthStatus = () => {
      const storedUser = localStorage.getItem('lillaflora_admin_user');
      const token = localStorage.getItem('lillaflora_admin_token');
      
      if (storedUser && token) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('lillaflora_admin_user');
          localStorage.removeItem('lillaflora_admin_token');
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    setLoading(true);
    
    try {
      // For now, use hardcoded credentials (in production, this would be an API call)
      if (username === 'admin' && password === '0UcO0jzyqAyrKYJZ') {
        const userData: User = {
          id: 1,
          username: 'admin',
          email: 'admin@lillaflora.nu',
          role: 'admin'
        };
        
        // Use the actual API token (this should match the backend configuration)
        const token = '8f2e4c7b9d1a6f3e8b2c5d9f7a4e6b8c1d9f2e7a5b8c3f6e9d2a7b4e5c8f1a6d3b9e2c7f4a5d8b1e6c3f9d2a7e4b5c8f6a1d9e2b7c4f5a8d1b3e6c9f2a5d7e4b1c8f6a3d9e2b5c7f4a8d1e6c3f9d2a7e4b5c8f6a1d9e2b7c4f5';

        setUser(userData);
        localStorage.setItem('lillaflora_admin_user', JSON.stringify(userData));
        localStorage.setItem('lillaflora_admin_token', token);
        
        setLoading(false);
        return true;
      } else {
        setLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('lillaflora_admin_user');
    localStorage.removeItem('lillaflora_admin_token');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;